/**
 * Shared add-to-cart + immediate cart UI refresh.
 * Morph the drawer once and update the header count from the add response.
 * Drawer stays closed by default — show a short toast instead; customers open
 * the cart when they want.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { lockScroll, releasePageScrollHard } from '@theme/utilities';

const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

/** Scroll Y captured when cart opens — restored on close so shop/collection stays put. */
let cartOpenScrollY = null;

function getPageScrollY() {
  try {
    const wrapper = document.querySelector('.page-wrapper');
    if (
      wrapper instanceof HTMLElement &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      return wrapper.scrollTop || 0;
    }
  } catch (_) {
    /* ignore */
  }
  return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function setPageScrollY(y) {
  const top = Math.max(0, Number(y) || 0);
  try {
    const wrapper = document.querySelector('.page-wrapper');
    if (
      wrapper instanceof HTMLElement &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      wrapper.scrollTo({ top, behavior: 'instant' });
      return;
    }
  } catch (_) {
    /* ignore */
  }
  window.scrollTo({ top, behavior: 'instant' });
  document.documentElement.scrollTop = top;
  document.body.scrollTop = top;
}

function rememberCartOpenScroll() {
  if (cartOpenScrollY == null) cartOpenScrollY = getPageScrollY();
}

function restoreCartOpenScroll() {
  const y = cartOpenScrollY;
  cartOpenScrollY = null;
  if (y == null || !Number.isFinite(y)) return;
  const apply = () => setPageScrollY(y);
  apply();
  requestAnimationFrame(apply);
  setTimeout(apply, 50);
  setTimeout(apply, 200);
  setTimeout(apply, 500);
}

function shopRoot() {
  return (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
}

function cartAddUrl() {
  if (window.Theme?.routes?.cart_add_url) return window.Theme.routes.cart_add_url;
  return `${shopRoot()}cart/add.js`;
}

function cartJsUrl() {
  if (window.Theme?.routes?.cart_url) return `${window.Theme.routes.cart_url}.js`;
  return `${shopRoot()}cart.js`;
}

function collectCartSectionIds() {
  const ids = new Set([CART_DRAWER_SECTION_ID]);
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  if (document.getElementById('shopify-section-cart-drawer-section')) {
    ids.add(CART_DRAWER_SECTION_ID);
  }
  return Array.from(ids);
}

function countRealItems(cart) {
  const items = cart?.items || [];
  if (!items.length) return Number(cart?.item_count || 0);
  return items.reduce((sum, item) => {
    if (item?.properties?._adawat_sale_probe) return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

function currentShownCount() {
  const bubble = document.querySelector('[data-adawat-cart-count], .cart-bubble__text, [ref="cartBubbleCount"]');
  const raw = bubble?.getAttribute?.('data-count') || bubble?.textContent || '0';
  const n = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

export function applyCartCount(count) {
  const n = Math.max(0, Number(count) || 0);
  const text = String(n);
  document.querySelectorAll('[data-adawat-cart-count]').forEach((el) => {
    el.textContent = text;
    el.dataset.count = text;
  });
  document.querySelectorAll('cart-icon [ref="cartBubbleCount"], .cart-bubble__text').forEach((el) => {
    el.textContent = n < 100 ? text : '';
    el.classList.toggle('hidden', n === 0);
  });
}

async function fetchJson(url, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...rest,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...(extraHeaders || {}),
    },
    cache: 'no-store',
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

export async function fetchCartState() {
  let cart = await fetchCartUntil(0);
  await clearLuckyBagsAfterCheckout(cart);
  cart = (await clearLuckyBagsWhenDisabled(cart)) || cart;
  cart = (await clearLuckyDiceWhenDisabled(cart)) || cart;
  return (await clearSpinningWheelWhenDisabled(cart)) || cart;
}

function isLuckyBagsEnabled() {
  const flag = window.__ADAWAT_LUCKY_BAGS_ENABLED__;
  if (flag === false || flag === 0 || flag === '0') return false;
  return flag === true || flag === 1 || flag === '1' || flag == null;
}

function isLuckyDiceEnabled() {
  const flag = window.__ADAWAT_LUCKY_DICE_ENABLED__;
  return flag === true || flag === 1 || flag === '1';
}

function isSpinningWheelEnabled() {
  const flag = window.__ADAWAT_SPINNING_WHEEL_ENABLED__;
  return flag === true || flag === 1 || flag === '1';
}

function isSpinWheelDiscountCode(code) {
  const value = String(code || '').trim().toUpperCase();
  return value.startsWith('ADSW-');
}

async function clearSpinningWheelWhenDisabled(cart) {
  if (isSpinningWheelEnabled() || !cart) return cart;
  const attrs = cart.attributes || {};
  const hasAttrs =
    Boolean(String(attrs.spinning_wheel_code || '').trim()) ||
    Boolean(String(attrs.spinning_wheel_pct || '').trim()) ||
    Boolean(String(attrs.spinning_wheel_gift || '').trim());
  const allCodes = [];
  (cart.discount_codes || []).forEach((d) => {
    const code = String(d?.code || d || '').trim();
    if (code) allCodes.push(code);
  });
  const wheelCodes = allCodes.filter((c) => isSpinWheelDiscountCode(c));
  const keepCodes = allCodes.filter((c) => !isSpinWheelDiscountCode(c));
  if (!hasAttrs && !wheelCodes.length) return cart;
  try {
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
    const payload = {
      attributes: { spinning_wheel_pct: '', spinning_wheel_code: '', spinning_wheel_gift: '' },
      sections: CART_DRAWER_SECTION_ID,
    };
    if (wheelCodes.length) payload.discount = keepCodes.join(',');
    const { data } = await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    try {
      localStorage.removeItem('adawat-spinning-wheel-played');
    } catch (_) {}
    return data || cart;
  } catch (_) {
    return cart;
  }
}

function isBagsDiscountCode(value) {
  const t = String(value || '').trim().toUpperCase();
  return /^ADAWAT\d*$/.test(t) || t.includes('ADAWAT');
}

function isDiceDiscountCode(value) {
  const t = String(value || '').trim().toUpperCase();
  if (!t) return false;
  /* Hard ADLD-* codes, Admin titles "Lucky Dice …", and legacy DICE*. */
  if (t.startsWith('ADLD-') || t.startsWith('ADLD')) return true;
  if (t.includes('LUCKY DICE') || t.includes('DICE DISCOUNT') || t.includes('خصم النرد')) return true;
  if (/^DICE\d*$/.test(t) || t.includes('DICE')) return true;
  return false;
}

/**
 * When Lucky Bags is off in Store Control, strip ADAWAT codes + attributes
 * so leftover wins do not keep discounting the cart.
 */
async function clearLuckyBagsWhenDisabled(cart) {
  if (isLuckyBagsEnabled() || !cart) return cart;

  const attrs = cart.attributes || {};
  const hasAttrs =
    Boolean(String(attrs.lucky_bags_code || '').trim()) || Boolean(String(attrs.lucky_bags_pct || '').trim());

  const allCodes = [];
  (cart.discount_codes || []).forEach((d) => {
    const code = String(d?.code || d || '').trim();
    if (code) allCodes.push(code);
  });
  (cart.cart_level_discount_applications || []).forEach((d) => {
    if (String(d?.type || '') !== 'discount_code' && d?.type != null) return;
    const title = String(d?.title || d?.code || '').trim();
    if (title && !allCodes.some((c) => c.toUpperCase() === title.toUpperCase())) {
      allCodes.push(title);
    }
  });

  const bagsCodes = allCodes.filter((c) => isBagsDiscountCode(c));
  const keepCodes = allCodes.filter((c) => !isBagsDiscountCode(c));
  const appsHaveBags = (cart.cart_level_discount_applications || []).some((d) =>
    isBagsDiscountCode(d?.title || d?.code)
  );

  if (!hasAttrs && !bagsCodes.length && !appsHaveBags) return cart;

  try {
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
    const payload = {
      attributes: { lucky_bags_pct: '', lucky_bags_code: '' },
      sections: CART_DRAWER_SECTION_ID,
    };
    if (bagsCodes.length || appsHaveBags) {
      payload.discount = keepCodes.join(',');
    }
    const { data } = await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    document.querySelectorAll('[data-adawat-cart-lucky]').forEach((el) => el.remove());
    try {
      window.localStorage.removeItem('adawat-lucky-bags-played');
    } catch (_) {
      /* ignore */
    }
    const sections = data?.sections || (await fetchDrawerSections());
    await refreshCartUi(sections, data?.item_count, data?.items);
    return data || cart;
  } catch (_) {
    return cart;
  }
}

/**
 * When Lucky Dice is off in Store Control, strip DICE codes + attributes
 * so leftover wins do not keep discounting the cart.
 */
async function clearLuckyDiceWhenDisabled(cart) {
  if (isLuckyDiceEnabled() || !cart) return cart;

  const attrs = cart.attributes || {};
  const hasAttrs =
    Boolean(String(attrs.lucky_dice_code || '').trim()) || Boolean(String(attrs.lucky_dice_pct || '').trim());

  const allCodes = [];
  (cart.discount_codes || []).forEach((d) => {
    const code = String(d?.code || d || '').trim();
    if (code) allCodes.push(code);
  });
  (cart.cart_level_discount_applications || []).forEach((d) => {
    if (String(d?.type || '') !== 'discount_code' && d?.type != null) return;
    const title = String(d?.title || d?.code || '').trim();
    if (title && !allCodes.some((c) => c.toUpperCase() === title.toUpperCase())) {
      allCodes.push(title);
    }
  });

  const diceCodes = allCodes.filter((c) => isDiceDiscountCode(c));
  const keepCodes = allCodes.filter((c) => !isDiceDiscountCode(c));
  const appsHaveDice = (cart.cart_level_discount_applications || []).some((d) =>
    isDiceDiscountCode(d?.title || d?.code)
  );

  if (!hasAttrs && !diceCodes.length && !appsHaveDice) return cart;

  try {
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
    const payload = {
      attributes: { lucky_dice_pct: '', lucky_dice_code: '' },
      sections: CART_DRAWER_SECTION_ID,
    };
    if (diceCodes.length || appsHaveDice) {
      payload.discount = keepCodes.join(',');
    }
    const { data } = await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    document.querySelectorAll('[data-adawat-cart-lucky-dice]').forEach((el) => el.remove());
    try {
      window.localStorage.removeItem('adawat-lucky-dice-played');
    } catch (_) {
      /* ignore */
    }
    const sections = data?.sections || (await fetchDrawerSections());
    await refreshCartUi(sections, data?.item_count, data?.items);
    return data || cart;
  } catch (_) {
    return cart;
  }
}

/**
 * After an order, Shopify starts a fresh cart. Clear leftover Lucky Bags
 * attributes so the yellow label does not linger on the next empty cart.
 * Keep the label while the promo code is still on the cart (even if empty).
 */
async function clearLuckyBagsAfterCheckout(cart) {
  if (!cart) return;
  const attrs = cart.attributes || {};
  const pct = String(attrs.lucky_bags_pct || '').trim();
  const code = String(attrs.lucky_bags_code || '').trim();
  if (!pct && !code) return;

  const want = code.toUpperCase();
  const onApps = (cart.cart_level_discount_applications || []).some((d) => {
    const t = String(d.title || d.code || '').toUpperCase();
    return t.includes('ADAWAT') || (want && t.includes(want));
  });
  const onCodes = (cart.discount_codes || []).some((d) => {
    const c = String(d.code || d || '').toUpperCase();
    const ok = d?.applicable === true || d?.applicable === undefined;
    return ok && (c.includes('ADAWAT') || (want && c.includes(want)));
  });

  if (onApps || onCodes) return;

  /* No Lucky Bags discount on cart anymore (e.g. after checkout) → drop label. */
  try {
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
    await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { lucky_bags_pct: '', lucky_bags_code: '' },
      }),
    });
    document.querySelectorAll('[data-adawat-cart-lucky]').forEach((el) => el.remove());
  } catch (_) {
    /* ignore */
  }
}

/** Re-apply a won Lucky Bags code if attributes exist but the discount is not live. */
async function ensureLuckyBagsDiscount(cart) {
  if (!isLuckyBagsEnabled()) return cart;
  if (!cart?.item_count) return cart;
  const code = String(cart.attributes?.lucky_bags_code || '').trim();
  if (!code) return cart;
  const want = code.toUpperCase();
  const applied = (cart.cart_level_discount_applications || []).some((d) => {
    const t = String(d.title || d.code || '').toUpperCase();
    return t.includes(want) || t.includes('ADAWAT');
  });
  const applicable = (cart.discount_codes || []).some((d) => {
    const c = String(d.code || d || '').toUpperCase();
    return c === want && d.applicable === true;
  });
  if (applied || applicable) return cart;
  try {
    return (await applyDiscountCode(code, { openDrawer: false })) || cart;
  } catch (_) {
    return cart;
  }
}

/** Re-apply a won Lucky Dice code if attributes exist but the discount is not live. */
async function ensureLuckyDiceDiscount(cart) {
  if (!isLuckyDiceEnabled()) return cart;
  if (!cart?.item_count) return cart;
  const code = String(cart.attributes?.lucky_dice_code || '').trim();
  if (!code) return cart;
  const want = code.toUpperCase();
  const applied = (cart.cart_level_discount_applications || []).some((d) => {
    const t = String(d.title || d.code || '').toUpperCase();
    return t.includes(want) || isDiceDiscountCode(t);
  });
  const applicable = (cart.discount_codes || []).some((d) => {
    const c = String(d.code || d || '').toUpperCase();
    return c === want && d.applicable === true;
  });
  if (applied || applicable) return cart;
  try {
    return (await applyDiscountCode(code, { openDrawer: false })) || cart;
  } catch (_) {
    return cart;
  }
}

async function ensureSpinningWheelDiscount(cart) {
  if (!isSpinningWheelEnabled()) return cart;
  if (!cart?.item_count) return cart;
  const code = String(cart.attributes?.spinning_wheel_code || '').trim();
  if (!code) return cart;
  const want = code.toUpperCase();
  const applied = (cart.cart_level_discount_applications || []).some((d) => {
    const t = String(d.title || d.code || '').toUpperCase();
    return t.includes(want) || isSpinWheelDiscountCode(t);
  });
  const applicable = (cart.discount_codes || []).some((d) => {
    const c = String(d.code || d || '').toUpperCase();
    return c === want && d.applicable === true;
  });
  if (applied || applicable) return cart;
  try {
    return (await applyDiscountCode(code, { openDrawer: false })) || cart;
  } catch (_) {
    return cart;
  }
}

async function fetchCartUntil(minCount) {
  let cart = { items: [], item_count: 0 };
  for (let i = 0; i < 5; i += 1) {
    const { res, data } = await fetchJson(`${cartJsUrl()}?t=${Date.now()}-${i}`);
    if (res.ok && data) cart = data;
    if (!minCount || Number(cart.item_count || 0) >= minCount) return cart;
    await new Promise((resolve) => setTimeout(resolve, 80 * (i + 1)));
  }
  return cart;
}

async function fetchDrawerSections() {
  try {
    const url = `${shopRoot()}?sections=${CART_DRAWER_SECTION_ID}&t=${Date.now()}`;
    const { res, data } = await fetchJson(url);
    if (res.ok && data?.[CART_DRAWER_SECTION_ID]) return data;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // iOS Safari and slower Android Chrome often paint after rAF.
        setTimeout(resolve, 48);
      });
    });
  });
}

function isDrawerVisible(drawer) {
  if (!drawer) return false;
  const panel = /** @type {HTMLDialogElement | null} */ (drawer.querySelector('dialog'));
  if (!panel) return false;

  // Cart is data-modal — only count as open when actually in the top layer.
  try {
    if (typeof panel.matches === 'function' && panel.matches(':modal')) return true;
  } catch (_) {
    /* :modal unsupported */
  }

  if (!panel.open) return false;
  const rect = panel.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1 && drawer.hasAttribute('open');
}

/**
 * Open the cart drawer. Prefer ThemeDrawer.open() (pairs scroll-lock with close).
 * After ATC morph, showModal can fail on the first try — retry + safe fallback.
 */
export function openCartDrawer() {
  hideAddToast();
  rememberCartOpenScroll();
  const drawer = /** @type {HTMLElement & { open?: Function } | null} */ (
    document.querySelector('theme-drawer#cart-drawer, theme-drawer[id="cart-drawer"], #cart-drawer')
  );
  if (!drawer) return false;
  if (isDrawerVisible(drawer)) return true;

  const panel = /** @type {HTMLDialogElement | null} */ (drawer.querySelector('dialog'));

  // Stale host [open] after morph (dialog dropped from top layer) blocks a real reopen.
  if (drawer.hasAttribute('open') && !isDrawerVisible(drawer)) {
    drawer.removeAttribute('open');
    if (panel?.open) {
      try {
        panel.close();
      } catch (_) {
        /* ignore */
      }
    }
  }

  if (typeof drawer.open === 'function') {
    try {
      drawer.open();
    } catch (_) {
      /* fall through to fallback */
    }
  }

  if (isDrawerVisible(drawer)) return true;

  return openCartDrawerFallback(drawer, panel);
}

/**
 * Last-resort open after morph / before custom-element upgrade.
 * Always lockScroll so close → unlockScroll still clears homepage scroll.
 * @param {HTMLElement} drawer
 * @param {HTMLDialogElement | null} panel
 */
function openCartDrawerFallback(drawer, panel) {
  if (!panel) return false;

  const ua = navigator.userAgent || '';
  const isFragileMobile =
    /Android/i.test(ua) ||
    (/Linux;.*Mobile/i.test(ua) && !/iPhone|iPad|iPod/i.test(ua)) ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 989px) and (hover: none), (max-width: 989px) and (pointer: coarse)').matches);

  const show = () => {
    try {
      if (isFragileMobile && typeof panel.show === 'function') {
        // Never showModal on fragile mobiles — UA scroll-lock often never releases.
        if (!panel.open) panel.show();
      } else if (typeof panel.showModal === 'function') {
        if (panel.open) {
          try {
            panel.close();
          } catch (_) {
            /* ignore */
          }
        }
        panel.showModal();
      } else if (typeof panel.show === 'function' && !panel.open) {
        panel.show();
      } else {
        panel.setAttribute('open', '');
      }
    } catch (_) {
      panel.setAttribute('open', '');
    }
  };

  show();
  if (!panel.open) {
    setTimeout(show, 50);
  }

  drawer.setAttribute('open', '');

  // Do NOT lockScroll on fragile mobile (frozen homepage) or laptop
  // (.page-wrapper overflow:hidden jumps scroll to the top).
  const isDesktopPageWrapper =
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 990px)').matches;
  if (!isFragileMobile && !isDesktopPageWrapper) {
    lockScroll(panel);
  }

  if (isFragileMobile) {
    let scrim = document.getElementById('adawat-cart-scrim');
    if (!scrim) {
      scrim = document.createElement('button');
      scrim.type = 'button';
      scrim.id = 'adawat-cart-scrim';
      scrim.className = 'adawat-cart-scrim';
      scrim.setAttribute('aria-label', 'Close cart');
      document.body.appendChild(scrim);
    }
    scrim.hidden = false;
    scrim.onclick = () => {
      if (typeof drawer.close === 'function') drawer.close();
    };
  }

  drawer.dispatchEvent(new CustomEvent('theme-drawer:open', { bubbles: true }));

  return isDrawerVisible(drawer) || Boolean(panel.open);
}

function restoreDrawerAfterMorph() {
  openCartDrawer();
}

function cartFromCount(count, items = []) {
  const payload = {
    item_count: count,
    items,
    token: '',
    total_price: 0,
    currency: '',
  };
  try {
    return CartLinesUpdateEvent.createCartFromAjaxResponse(payload);
  } catch (_) {
    return { totalQuantity: count };
  }
}

function cartChangeUrl() {
  if (window.Theme?.routes?.cart_change_url) return window.Theme.routes.cart_change_url;
  return `${shopRoot()}cart/change.js`;
}

function unlockCartItems() {
  document.querySelectorAll('cart-items-component').forEach((el) => {
    try {
      customElements.upgrade(el);
    } catch (_) {
      /* ignore */
    }
    el.classList.remove('cart-items-disabled');
  });
}

function unlockCartItemsSoon() {
  unlockCartItems();
  requestAnimationFrame(unlockCartItems);
  setTimeout(unlockCartItems, 80);
  setTimeout(unlockCartItems, 250);
}

export async function refreshCartUi(sections, itemCount, items) {
  if (itemCount != null) applyCartCount(itemCount);
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      detail: {
        ...(itemCount != null ? { itemCount, item_count: itemCount } : {}),
        ...(items?.length ? { items } : {}),
      },
    })
  );

  const sectionEntries = sections && typeof sections === 'object' ? Object.entries(sections) : [];
  let morphedAny = false;
  for (const [sectionId, html] of sectionEntries) {
    if (!html || !sectionId) continue;
    try {
      await morphSection(sectionId, html, {
        mode: sectionId === CART_DRAWER_SECTION_ID ? 'hydration' : 'full',
      });
      morphedAny = true;
    } catch (error) {
      console.warn('[adawat-cart] Section morph failed:', sectionId, error);
    }
  }

  if (!morphedAny || !sections?.[CART_DRAWER_SECTION_ID]) {
    try {
      await sectionRenderer.renderSection(CART_DRAWER_SECTION_ID, {
        cache: false,
        mode: 'hydration',
      });
    } catch (error) {
      console.warn('[adawat-cart] Drawer refresh failed:', error);
    }
  }
  unlockCartItemsSoon();
}

function resolveLineFromDom(btn) {
  if (!(btn instanceof Element)) return { key: '', line: 0 };
  const key = btn.getAttribute('data-adawat-remove-key') || '';
  const attrLine = Number(btn.getAttribute('data-adawat-remove-line')) || 0;
  const row = btn.closest('[ref="cartItemRows[]"], .cart-items__table-row, [data-key]');
  const scope = btn.closest('#cart-drawer, cart-items-component, .cart-page') || document;
  const rows = Array.from(scope.querySelectorAll('[ref="cartItemRows[]"]'));
  let line = 0;
  if (row && rows.includes(row)) line = rows.indexOf(row) + 1;
  if (!line) line = attrLine;
  return { key, line };
}

export async function removeCartLine({ key, line } = {}) {
  const payload = {
    quantity: 0,
    sections: collectCartSectionIds().join(','),
    sections_url: window.location.pathname,
  };
  if (Number(line) > 0) payload.line = Number(line);
  else if (key) payload.id = key;
  else return null;

  const drawerWasOpen = isDrawerVisible(document.querySelector('#cart-drawer'));

  const { res, data } = await fetchJson(cartChangeUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok || data.errors || data.status) {
    const msg = data.errors || data.description || data.message || 'Could not remove item';
    throw new Error(typeof msg === 'string' ? msg : 'Could not remove item');
  }
  const count = Number(data.item_count || 0);
  applyCartCount(count);
  await refreshCartUi(data.sections, count, data.items);
  applyCartCount(count);
  if (drawerWasOpen) {
    await waitForPaint();
    openCartDrawer();
  }
  return data;
}

async function waitIfSaleProbe() {
  window.AdawatAbortSaleProbe?.();
  const started = Date.now();
  while (window.__ADAWAT_SALE_PROBE__ && Date.now() - started < 2500) {
    await new Promise((resolve) => setTimeout(resolve, 40));
  }
}

const ADD_TOAST_MS = 5000;
let addToastTimer = 0;

function isRtlDoc() {
  try {
    return document.documentElement.getAttribute('dir') === 'rtl';
  } catch (_) {
    return false;
  }
}

function ensureAddToast() {
  let el = document.getElementById('AdawatCartAddToast');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'AdawatCartAddToast';
  el.className = 'adawat-cart-toast';
  el.hidden = true;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML =
    '<button type="button" class="adawat-cart-toast__backdrop" data-adawat-toast-close aria-label="Close"></button>' +
    '<div class="adawat-cart-toast__card">' +
    '<button type="button" class="adawat-cart-toast__close" data-adawat-toast-close aria-label="Close">' +
    '<span aria-hidden="true">×</span>' +
    '</button>' +
    '<span class="adawat-cart-toast__icon" aria-hidden="true">' +
    '<svg viewBox="0 0 72 72" width="72" height="72" fill="none">' +
    '<circle cx="36" cy="36" r="30" stroke="currentColor" stroke-width="4"/>' +
    '<path d="M22 37.2 31.2 46 51 25.5" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>' +
    '</span>' +
    '<div class="adawat-cart-toast__copy">' +
    '<p class="adawat-cart-toast__title" data-adawat-toast-title></p>' +
    '<p class="adawat-cart-toast__meta" data-adawat-toast-meta></p>' +
    '</div>' +
    '<div class="adawat-cart-toast__actions">' +
    '<button type="button" class="adawat-cart-toast__btn" data-adawat-toast-cart></button>' +
    '<button type="button" class="adawat-cart-toast__btn adawat-cart-toast__btn--secondary" data-adawat-toast-continue></button>' +
    '</div>' +
    '</div>';
  el.querySelectorAll('[data-adawat-toast-close]').forEach((btn) => {
    btn.addEventListener('click', () => hideAddToast());
  });
  el.querySelector('[data-adawat-toast-cart]')?.addEventListener('click', () => {
    hideAddToast();
    openCartDrawer();
  });
  el.querySelector('[data-adawat-toast-continue]')?.addEventListener('click', () => hideAddToast());
  document.body.appendChild(el);
  return el;
}

export function hideAddToast() {
  const el = document.getElementById('AdawatCartAddToast');
  if (addToastTimer) {
    window.clearTimeout(addToastTimer);
    addToastTimer = 0;
  }
  if (!el) return;
  el.classList.remove('is-visible');
  el.hidden = true;
}

/**
 * Brief confirmation after add-to-cart. Does not open the cart drawer.
 * @param {{ title?: string, quantity?: number } | undefined} info
 */
export function showAddToast(info) {
  const el = ensureAddToast();
  const rtl = isRtlDoc();
  const title = el.querySelector('[data-adawat-toast-title]');
  const meta = el.querySelector('[data-adawat-toast-meta]');
  const cartBtn = el.querySelector('[data-adawat-toast-cart]');
  const contBtn = el.querySelector('[data-adawat-toast-continue]');
  const closeBtn = el.querySelector('.adawat-cart-toast__close');
  const productTitle = String(info?.title || '').trim();
  const qty = Math.max(1, Number(info?.quantity) || 1);
  if (title) title.textContent = rtl ? 'تمت الإضافة إلى السلة' : 'Added to cart';
  if (meta) {
    if (productTitle) {
      meta.hidden = false;
      meta.textContent = rtl ? `${productTitle} · الكمية ${qty}` : `${productTitle} · Qty ${qty}`;
    } else {
      meta.hidden = true;
      meta.textContent = '';
    }
  }
  if (cartBtn) cartBtn.textContent = rtl ? 'عرض السلة' : 'View cart';
  if (contBtn) contBtn.textContent = rtl ? 'متابعة التسوق' : 'Continue shopping';
  if (closeBtn) closeBtn.setAttribute('aria-label', rtl ? 'إغلاق' : 'Close');
  el.hidden = false;
  // Force reflow so the enter transition plays when re-shown quickly.
  void el.offsetWidth;
  el.classList.add('is-visible');
  if (addToastTimer) window.clearTimeout(addToastTimer);
  addToastTimer = window.setTimeout(() => hideAddToast(), ADD_TOAST_MS);
}

/**
 * @param {{ id: number, quantity?: number } | Array<{ id: number, quantity?: number }>} items
 */
export async function addItemsToCart(items, { openDrawer = false } = {}) {
  await waitIfSaleProbe();
  const list = (Array.isArray(items) ? items : [items]).map((item) => ({
    id: Number(item.id),
    quantity: Math.max(1, Number(item.quantity) || 1),
  }));
  const addedQty = list.reduce((sum, item) => sum + item.quantity, 0);
  const nextCount = currentShownCount() + addedQty;
  let deferred = {
    promise: Promise.resolve(),
    resolve() {},
    reject() {},
  };
  try {
    if (typeof CartLinesUpdateEvent.createPromise === 'function') {
      deferred = CartLinesUpdateEvent.createPromise();
    }
  } catch (_) {
    /* Shopify events helper is optional — never block the add. */
  }
  let settled = false;

  const finish = (payload) => {
    if (settled) return;
    settled = true;
    try {
      deferred.resolve(payload);
    } catch (_) {
      /* ignore */
    }
  };

  try {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: list.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );
  } catch (_) {
    /* ignore */
  }

  try {
    const payload = {
      items: list,
      sections: CART_DRAWER_SECTION_ID,
      sections_url: window.location.pathname,
    };

    const { res: addRes, data: addData } = await fetchJson(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!addRes.ok || addData.status) {
      const msg = addData.description || addData.message || 'Could not add to cart';
      const error = new Error(typeof msg === 'string' ? msg : 'Could not add to cart');
      const cart = await fetchCartUntil(0);
      finish({
        cart: cartFromCount(cart.item_count, cart.items),
        detail: {
          didError: true,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'adawat-cart',
        },
      });
      throw error;
    }

    applyCartCount(nextCount);
    // Confirm immediately — don't wait on drawer morph / gift cleanup.
    if (openDrawer) {
      /* drawer opens after UI refresh below */
    } else {
      const added = Array.isArray(addData.items) ? addData.items : [];
      const first = added[0] || {};
      showAddToast({
        title: first.product_title || first.title || '',
        quantity: added.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0) || addedQty,
      });
    }

    let sections = addData.sections || null;
    if (!sections?.[CART_DRAWER_SECTION_ID]) {
      sections = (await fetchDrawerSections()) || sections;
    }
    await refreshCartUi(sections, nextCount, addData.items);
    applyCartCount(nextCount);
    unlockCartItemsSoon();

    try {
      let cart = await fetchCartUntil(Math.max(1, nextCount));
      cart = (await clearLuckyBagsWhenDisabled(cart)) || cart;
      cart = (await clearLuckyDiceWhenDisabled(cart)) || cart;
      cart = (await clearSpinningWheelWhenDisabled(cart)) || cart;
      await ensureLuckyBagsDiscount(cart);
      await ensureLuckyDiceDiscount(cart);
      await ensureSpinningWheelDiscount(cart);
    } catch (_) {
      /* ignore */
    }

    if (openDrawer) {
      await customElements.whenDefined('theme-drawer').catch(() => {});
      await waitForPaint();
      openCartDrawer();
      // Morph can leave the drawer closed on desktop — reopen after paint.
      if (!isDrawerVisible(document.querySelector('#cart-drawer'))) {
        await waitForPaint();
        openCartDrawer();
      }
      if (!isDrawerVisible(document.querySelector('#cart-drawer'))) {
        await new Promise((resolve) => setTimeout(resolve, 80));
        openCartDrawer();
      }
    }

    finish({
      cart: cartFromCount(nextCount, addData.items || []),
      detail: {
        items: addData.items,
        sections,
        didError: false,
        itemCount: nextCount,
        source: 'adawat-cart',
      },
    });

    fetchCartUntil(nextCount).then((cart) => {
      const confirmed = countRealItems(cart);
      applyCartCount(confirmed);
    });

    return addData;
  } catch (err) {
    if (!settled) {
      settled = true;
      try {
        deferred.reject(err);
      } catch (_) {
        /* ignore */
      }
    }
    throw err;
  }
}

export async function applyDiscountCode(code, { openDrawer = true, attributes } = {}) {
  const trimmed = String(code || '').trim();
  if (!trimmed) return null;
  if (isBagsDiscountCode(trimmed) && !isLuckyBagsEnabled()) return null;
  if (isDiceDiscountCode(trimmed) && !isLuckyDiceEnabled()) return null;
  if (isSpinWheelDiscountCode(trimmed) && !isSpinningWheelEnabled()) return null;
  const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
  const sectionIds = collectCartSectionIds();
  const payload = {
    discount: trimmed,
    sections: sectionIds,
  };
  if (attributes && typeof attributes === 'object') {
    payload.attributes = attributes;
  }
  let { data } = await fetchJson(cartUpdateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const codeUpper = trimmed.toUpperCase();
  const isLuckyApplied = (cart) => {
    if (!cart) return false;
    const onApps = (cart.cart_level_discount_applications || []).some((d) => {
      const t = String(d.title || d.code || '').toUpperCase();
      return t.includes(codeUpper) || t.includes('ADAWAT');
    });
    if (onApps) return true;
    return (cart.discount_codes || []).some((d) => {
      const c = String(d.code || d || '').toUpperCase();
      return c === codeUpper && d.applicable === true;
    });
  };

  if (!isLuckyApplied(data)) {
    /* Cookie-based apply as fallback when Ajax discount lands as applicable:false. */
    try {
      await fetch(`${shopRoot()}discount/${encodeURIComponent(trimmed)}`, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } catch (_) {
      /* ignore */
    }
    const retry = await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    data = retry.data || data;
  }

  const sections = data?.sections || (await fetchDrawerSections());
  await refreshCartUi(sections, data?.item_count);
  if (openDrawer) {
    await customElements.whenDefined('theme-drawer').catch(() => {});
    await waitForPaint();
    openCartDrawer();
    if (!isDrawerVisible(document.querySelector('#cart-drawer'))) {
      await waitForPaint();
      openCartDrawer();
    }
  }
  return data;
}

window.AdawatCart = {
  add: addItemsToCart,
  remove: removeCartLine,
  refresh: refreshCartUi,
  open: openCartDrawer,
  applyCount: applyCartCount,
  fetch: fetchCartState,
  applyDiscount: applyDiscountCode,
  showAddToast,
  hideAddToast,
};

function isAddToCartForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.matches('[data-adawat-product-form]')) return false;
  if (form.matches('[data-type="add-to-cart-form"]')) return true;
  const action = form.getAttribute('action') || '';
  return /\/cart\/add/i.test(action);
}

const addingForms = new WeakSet();

function queueAddFromForm(form) {
  if (!(form instanceof HTMLFormElement) || addingForms.has(form)) return false;
  const id = Number(form.querySelector('[name="id"]')?.value);
  const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
  if (!id) return false;
  addingForms.add(form);
  addItemsToCart({ id, quantity })
    .catch((error) => {
      console.warn('[adawat-cart] Add to cart failed:', error);
    })
    .finally(() => addingForms.delete(form));
  return true;
}

function isCardAddButton(el) {
  if (!(el instanceof Element)) return false;
  if (el.matches('a, [disabled], [aria-disabled="true"]')) return false;
  if (el.closest('[data-adawat-product-form]')) return false;
  return Boolean(
    el.closest(
      'button[type="submit"][name="add"], button[type="submit"].adawat-product-card__add-btn, .adawat-product-card__add-btn[type="submit"]'
    )
  );
}

function flashCardAddButton(btn) {
  if (!(btn instanceof HTMLElement)) return;
  if (!btn.classList.contains('adawat-product-card__add-btn')) return;
  const isPhone = window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches;
  if (!isPhone) return;

  const prevTimer = Number(btn.dataset.adawatAddingTimer || 0);
  if (prevTimer) window.clearTimeout(prevTimer);
  btn.classList.add('is-adding');
  const timer = window.setTimeout(() => {
    btn.classList.remove('is-adding');
    delete btn.dataset.adawatAddingTimer;
  }, 1000);
  btn.dataset.adawatAddingTimer = String(timer);
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest(
      'button[type="submit"][name="add"], button[type="submit"].adawat-product-card__add-btn, .adawat-product-card__add-btn[type="submit"]'
    );
    if (!btn || !isCardAddButton(btn) || btn.disabled) return;
    const form = btn.closest('form');
    if (!isAddToCartForm(form)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    flashCardAddButton(btn);
    queueAddFromForm(form);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!isAddToCartForm(form)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    queueAddFromForm(form);
  },
  true
);

let removingLine = false;
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest('[data-adawat-remove-line], [data-adawat-remove-key]');
    if (!btn || btn.hasAttribute('hidden')) return;
    if (!btn.closest('#cart-drawer, cart-items-component, .cart-page')) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    if (removingLine) return;

    const { key, line } = resolveLineFromDom(btn);
    if (!key && !line) return;

    const row = btn.closest('[ref="cartItemRows[]"], .cart-items__table-row');
    row?.classList.add('removing');

    removingLine = true;
    unlockCartItems();
    removeCartLine({ key, line })
      .catch((error) => {
        console.warn('[adawat-cart] Remove from cart failed:', error);
        row?.classList.remove('removing');
      })
      .finally(() => {
        removingLine = false;
        unlockCartItems();
      });
  },
  true
);

let cartRefreshQueued = false;
document.addEventListener('cart:refresh', () => {
  if (cartRefreshQueued) return;
  cartRefreshQueued = true;
  Promise.resolve()
    .then(() => refreshCartUi())
    .finally(() => {
      cartRefreshQueued = false;
    });
});

/**
 * After Admin discounts change + Save discounts, an existing cart can keep
 * pre-discount line prices until Shopify recalculates. Touch the cart once
 * per sitewide_sale.updated_at so automatic % applies to lines already in cart.
 */
async function recalculateCartForSaleSync() {
  const cfg = window.__ADAWAT_SITEWIDE_SALE__;
  if (!cfg || typeof cfg !== 'object') return;
  const enabled =
    cfg.enabled === true || cfg.enabled === 'true' || cfg.enabled === 1 || cfg.enabled === '1';
  const percent = Math.round(Number(cfg.percent) || 0);
  if (!enabled || percent < 1) return;

  const stamp = String(cfg.updated_at || cfg.shopify_id || percent);
  const key = 'adawat_cart_sale_recalc';
  try {
    if (window.sessionStorage.getItem(key) === stamp) return;
  } catch (_) {
    /* ignore */
  }

  try {
    const cart = await fetchCartUntil(0);
    if (!cart?.items?.length) {
      try {
        window.sessionStorage.setItem(key, stamp);
      } catch (_) {
        /* ignore */
      }
      return;
    }

    const hasLineDiscount = (cart.items || []).some(
      (item) =>
        Number(item.line_level_total_discount || 0) > 0 ||
        (item.line_level_discount_allocations || []).length > 0 ||
        Number(item.final_line_price) < Number(item.original_line_price)
    );
    const hasCartDiscount = (cart.cart_level_discount_applications || []).length > 0;
    if (hasLineDiscount || hasCartDiscount) {
      try {
        window.sessionStorage.setItem(key, stamp);
      } catch (_) {
        /* ignore */
      }
      return;
    }

    const updates = {};
    (cart.items || []).forEach((item) => {
      if (item?.key) updates[item.key] = item.quantity;
    });
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${shopRoot()}cart/update.js`;
    const { data } = await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections: CART_DRAWER_SECTION_ID,
      }),
    });
    const sections = data?.sections || (await fetchDrawerSections());
    await refreshCartUi(sections, data?.item_count, data?.items);
    try {
      window.sessionStorage.setItem(key, stamp);
      window.localStorage.setItem('adawat_sale_bust', String(cfg.updated_at || Date.now()));
    } catch (_) {
      /* ignore */
    }
    window.AdawatReapplySale?.();
  } catch (_) {
    /* ignore — cart still usable without forced recalc */
  }
}

/* Drop stale Lucky Bags label after checkout / when code is gone. */
fetchCartState()
  .then(() => recalculateCartForSaleSync())
  .catch(() => {});

// Android safety net: hard-unlock page scroll after cart closes.
document.addEventListener('theme-drawer:close', (event) => {
  if (/** @type {Element} */ (event.target)?.id !== 'cart-drawer') return;
  const scrim = document.getElementById('adawat-cart-scrim');
  if (scrim) {
    scrim.hidden = true;
    scrim.onclick = null;
  }
  releasePageScrollHard({ force: true });
  setTimeout(() => releasePageScrollHard({ force: true }), 50);
  setTimeout(() => releasePageScrollHard({ force: true }), 200);
  setTimeout(() => releasePageScrollHard({ force: true }), 500);
  // releasePageScrollHard can land at 0 after scroll-lock — put the page back.
  restoreCartOpenScroll();
});

/**
 * Android system back: close the cart like × instead of leaving the page.
 * Uses a history entry while the cart is open; popstate closes the drawer.
 */
(function bindCartAndroidBack() {
  const CART_STATE_KEY = 'adawatCart';
  let ignoreNextPop = false;
  let closedByPopstate = false;

  function cartDrawerEl() {
    return /** @type {(HTMLElement & { close?: Function }) | null} */ (
      document.querySelector('theme-drawer#cart-drawer, #cart-drawer')
    );
  }

  function isCartOpen() {
    const drawer = cartDrawerEl();
    if (!drawer) return false;
    if (drawer.hasAttribute('open')) return true;
    const panel = drawer.querySelector('dialog');
    return Boolean(panel && (panel.open || panel.hasAttribute('open')));
  }

  function pushCartHistory() {
    try {
      if (window.matchMedia('(min-width: 990px)').matches) return;
      if (history.state && history.state[CART_STATE_KEY]) return;
      rememberCartOpenScroll();
      history.pushState(
        {
          ...(history.state || {}),
          [CART_STATE_KEY]: 1,
          scrollTop: cartOpenScrollY ?? getPageScrollY(),
        },
        '',
        location.href
      );
    } catch (_) {
      /* ignore */
    }
  }

  function clearCartHistoryEntry() {
    // Already consumed by Android back — do not history.back() again.
    if (closedByPopstate) {
      closedByPopstate = false;
      return;
    }
    try {
      if (!(history.state && history.state[CART_STATE_KEY])) return;
      ignoreNextPop = true;
      history.back();
    } catch (_) {
      ignoreNextPop = false;
    }
  }

  function closeCartFromBack() {
    const drawer = cartDrawerEl();
    if (!drawer) return;
    closedByPopstate = true;
    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }
    const panel = /** @type {HTMLDialogElement | null} */ (drawer.querySelector('dialog'));
    drawer.removeAttribute('open');
    try {
      if (panel?.open) panel.close();
      else panel?.removeAttribute('open');
    } catch (_) {
      /* ignore */
    }
    const scrim = document.getElementById('adawat-cart-scrim');
    if (scrim) {
      scrim.hidden = true;
      scrim.onclick = null;
    }
    releasePageScrollHard({ force: true });
    restoreCartOpenScroll();
    closedByPopstate = false;
  }

  document.addEventListener('theme-drawer:open', (event) => {
    if (/** @type {Element} */ (event.target)?.id !== 'cart-drawer') return;
    rememberCartOpenScroll();
    pushCartHistory();
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (/** @type {Element} */ (event.target)?.id !== 'cart-drawer') return;
    // × / scrim / Escape — drop the dummy history entry without leaving the site.
    clearCartHistoryEntry();
  });

  window.addEventListener('popstate', () => {
    if (ignoreNextPop) {
      ignoreNextPop = false;
      return;
    }
    if (!isCartOpen()) return;
    // System back while cart is open → close cart, stay on this page.
    closeCartFromBack();
  });
})();
